/**
 * The helpdesk (tenant) this page belongs to. The database picks the tenant
 * from the x-tenant-host header that every Supabase request sends (see
 * supabase-client.ts), bootstrap loads its details.
 */
export interface CurrentTenant {
  id: number;
  slug: string;
  name: string;
  status: 'trialing' | 'active' | 'past_due' | 'expired' | 'suspended' | 'cancelled';
  trial_ends_at: string | null;
  plan_id: number | null;
  primary_host: string | null;
  is_owner: boolean;
  // platform-only pages (schedule log, platform console)
  viewer_is_platform_admin: boolean;
  // no plan needed (the platform's own helpdesk, given away helpdesks)
  billing_exempt: boolean;
  // see PlanLimits in @common/saas/plan-features
  limits: Record<string, number | boolean | null> | null;
  usage: {agents: number; mailboxes: number; ai_replies: number} | null;
}

export class UnknownTenantError extends Error {}

export const tenantHost = window.location.host;

let currentTenant: CurrentTenant | null = null;

export function setCurrentTenant(tenant: CurrentTenant | null) {
  currentTenant = tenant;
}

export function getCurrentTenant(): CurrentTenant | null {
  return currentTenant;
}

export function isPlatformAdmin() {
  return !!currentTenant?.viewer_is_platform_admin;
}

function requireTenant(): CurrentTenant {
  if (!currentTenant) {
    throw new UnknownTenantError(`No helpdesk at ${tenantHost}`);
  }
  return currentTenant;
}

/** Storage object paths start with the tenant id (storage RLS checks it). */
export function tenantStoragePath(path: string) {
  return `${requireTenant().id}/${path}`;
}

/** Realtime topics are per tenant, e.g. "helpdesk" -> "helpdesk:12". */
export function tenantTopic(channelName: string) {
  return `${channelName}:${requireTenant().id}`;
}
